// src/components/communications/EmailThread.jsx
// Modern email conversation view with compose functionality
import React, { useState, useRef } from 'react';
import {
  Send, Mail, Reply, Forward, Paperclip, ChevronDown, ChevronUp,
  Clock, CheckCheck, AlertCircle, User, ExternalLink, Trash2
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function EmailThread({ emails, clientId, clientName, onEmailSent }) {
  const [expandedEmails, setExpandedEmails] = useState(new Set([emails[0]?.id]));
  const [composing, setComposing] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [newEmail, setNewEmail] = useState({ subject: '', body: '' });
  const composeRef = useRef(null);

  const toggleExpand = (emailId) => {
    const newExpanded = new Set(expandedEmails);
    if (newExpanded.has(emailId)) {
      newExpanded.delete(emailId);
    } else {
      newExpanded.add(emailId);
    }
    setExpandedEmails(newExpanded);
  };

  const handleCompose = (email = null) => {
    if (email) {
      setReplyTo(email);
      setNewEmail({
        subject: email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject || ''}`,
        body: `\n\n---\nOn ${formatDate(email.created_at)}, ${email.direction === 'inbound' ? clientName : 'You'} wrote:\n${email.body || ''}`
      });
    } else {
      setReplyTo(null);
      setNewEmail({ subject: '', body: '' });
    }
    setComposing(true);
    setTimeout(() => composeRef.current?.focus(), 100);
  };

  const handleSend = async () => {
    if (!newEmail.subject.trim() || !newEmail.body.trim() || sending) return;

    setSending(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/comms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          clientId,
          type: 'email',
          subject: newEmail.subject.trim(),
          body: newEmail.body.trim(),
          direction: 'outbound'
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send email');
      }

      setNewEmail({ subject: '', body: '' });
      setComposing(false);
      setReplyTo(null);
      onEmailSent?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today - msgDay) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
    }
  };

  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString([], {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getDeliveryIcon = (email) => {
    const meta = email.metadata || {};
    if (email.direction === 'inbound') return null;

    if (meta.deliveryStatus === 'delivered') return <CheckCheck className="w-4 h-4 text-green-500" />;
    if (meta.deliveryStatus === 'failed') return <AlertCircle className="w-4 h-4 text-red-500" />;
    if (meta.deliveryStatus === 'sent') return <CheckCheck className="w-4 h-4 text-gray-400" />;
    return <Clock className="w-4 h-4 text-gray-300" />;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {emails.length} {emails.length === 1 ? 'email' : 'emails'}
          </span>
        </div>
        <button
          onClick={() => handleCompose()}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors shadow-sm"
        >
          <Mail className="w-4 h-4" />
          Compose
        </button>
      </div>

      {/* Email List */}
      <div className="flex-1 overflow-y-auto">
        {emails.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
            <Mail className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium">No emails yet</p>
            <p className="text-sm text-center mt-1">
              Send an email to start the conversation
            </p>
            <button
              onClick={() => handleCompose()}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
            >
              <Mail className="w-4 h-4" />
              Compose Email
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {emails.map((email) => {
              const isExpanded = expandedEmails.has(email.id);
              const isInbound = email.direction === 'inbound' || email.direction === 'in';

              return (
                <div key={email.id} className="bg-white hover:bg-gray-50 transition-colors">
                  {/* Email Header - Always visible */}
                  <button
                    onClick={() => toggleExpand(email.id)}
                    className="w-full px-4 py-4 flex items-start gap-4 text-left"
                  >
                    {/* Avatar */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isInbound ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'
                    }`}>
                      <User className="w-5 h-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`font-medium truncate ${!email.is_read && isInbound ? 'text-gray-900' : 'text-gray-700'}`}>
                          {isInbound ? clientName : 'You'}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getDeliveryIcon(email)}
                          <span className="text-sm text-gray-500">{formatDate(email.created_at)}</span>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                      <p className={`text-sm mt-0.5 ${!email.is_read && isInbound ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                        {email.subject || '(No subject)'}
                      </p>
                      {!isExpanded && (
                        <p className="text-sm text-gray-400 truncate mt-1">
                          {email.body?.substring(0, 100) || ''}
                        </p>
                      )}
                    </div>
                  </button>

                  {/* Expanded Email Body */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pl-18">
                      <div className="ml-14 p-4 bg-gray-50 rounded-xl border border-gray-100">
                        {/* Full Date */}
                        <p className="text-xs text-gray-400 mb-3">
                          {formatFullDate(email.created_at)}
                        </p>

                        {/* Body */}
                        <div className="prose prose-sm max-w-none">
                          <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                            {email.body || '(No content)'}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCompose(email);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          >
                            <Reply className="w-4 h-4" />
                            Reply
                          </button>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Forward className="w-4 h-4" />
                            Forward
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Compose Modal */}
      {composing && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-10">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Compose Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-900 to-slate-800">
              <h3 className="text-lg font-semibold text-white">
                {replyTo ? 'Reply' : 'New Email'}
              </h3>
              <button
                onClick={() => {
                  setComposing(false);
                  setReplyTo(null);
                  setNewEmail({ subject: '', body: '' });
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                &times;
              </button>
            </div>

            {/* Compose Form */}
            <div className="p-6 space-y-4">
              {/* To */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-500 w-16">To:</label>
                <div className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-gray-700">
                  {clientName}
                </div>
              </div>

              {/* Subject */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-500 w-16">Subject:</label>
                <input
                  ref={composeRef}
                  type="text"
                  value={newEmail.subject}
                  onChange={(e) => setNewEmail(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>

              {/* Body */}
              <div>
                <textarea
                  value={newEmail.body}
                  onChange={(e) => setNewEmail(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Write your message..."
                  rows={10}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                  <Paperclip className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setComposing(false);
                      setReplyTo(null);
                      setNewEmail({ subject: '', body: '' });
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={!newEmail.subject.trim() || !newEmail.body.trim() || sending}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-all ${
                      newEmail.subject.trim() && newEmail.body.trim() && !sending
                        ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg hover:shadow-xl'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {sending ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
